/**
 * The Forager Hut counter — the game's shop, and the only place goods become
 * Coins.
 *
 * It replaces the old Market TAB, which was a list of rows in the HUD footer:
 * you could sell your entire farm from anywhere on the map, without walking, and
 * there was nothing to buy at all. Now you build a Forager Hut, the keeper
 * stands at it, and you trade across his counter.
 *
 * The panel is deliberately shaped like the bag, because that is what it is: a
 * grid of the pack's 44px slot cells on YOUR side, the keeper's shelf on his, and
 * a counter between them holding whatever you have pushed across. Click a slot to
 * push ONE unit; the "All" chip pushes the whole stack. The counter shows the
 * exact Coins BEFORE you commit — nothing moves until the button is pressed.
 *
 * The client never decides a price. It renders the server's quote (unit × qty
 * from GET /forager/board) and the server re-derives the same number on trade;
 * if they disagree, the server's answer is the one that happened.
 */
import { el, toast } from '../../dom';
import { foragerApi, fieldApi, type ForagerBoard, type TradeItem, type SeedBoard, type SeedRate, type SellBackRow } from '../../game-api';
import { coinSellable, sellBackQuote } from './sell-back';
import { ApiError } from '../../../core/network/api';
import { dispatchAction, isDropped } from '../../action/dispatch';
import { closePlanter, openOverlay, refreshBag, type FarmCtx } from './context';
import { richText } from '../../ui/icons';
import { itemIconSrc } from '../../ui/item-icons';
import { clearWrap, qtyBadge, slotCell } from '../../ui/panel';
import { playSfx } from '../../audio/audio';

/**
 * Which way the goods are moving, and in which currency.
 *  `sell`   — yours → his, he pays Coins.
 *  `buy`    — his → yours, you pay Coins.
 *  `shrooms` — his → yours, you pay SHROOMS.
 *  `sellback` — yours → his, goods you BOUGHT with Shrooms, he pays Shrooms back
 *              (the price you paid, minus the sell-back fee — only the
 *              end price is shown, never the fee).
 *
 * The shroom side is new here, not on the server: SHROOM_STOCK, the
 * /forager/buy-shrooms route and its no-arbitrage test have all existed for a
 * while. The board simply never returned the shelf and this panel never drew it,
 * so nine items were buyable by no player. Owner: "in forager shop I don't see
 * items for shrooms."
 */
type Side = 'sell' | 'buy' | 'shrooms' | 'sellback';

/**
 * The counter has two halves. `trade` is the old panel; `seeds` is the press —
 * produce back into seed, which is the only thing standing between a player with
 * no Coins and a farm he cannot plant. It lives HERE rather than on the plot
 * because the keeper is the game's one trade counter, and this is a trade.
 */
type Tab = 'trade' | 'seeds';

/** What is currently on the counter: one item, a quantity, and its live quote. */
interface Offer {
  side: Side; item: TradeItem; qty: number;
  /** For `sellback`: the returnable lot and the fee rate, to price the counter. */
  back?: { row: SellBackRow; commission: number };
}

/**
 * The hut's own building controls, handed in by whoever opened the shop.
 *
 * Tapping the hut opens the counter directly, which means this panel is the ONLY
 * thing standing between the player and a hut he can no longer move or tear down
 * — there is no intermediate menu left to carry those rows. They are supplied as
 * callbacks rather than imported so the shop keeps knowing nothing about
 * placement or demolition (and so building-menu → forager-shop stays a one-way
 * import).
 */
export interface ManageHooks { name: string; onMove: () => void; onDemolish: () => void }

const pxImg = (src: string) =>
  el('img', 'w-8 h-8 object-contain', { src, style: 'image-rendering:pixelated' });

export function openForagerShop(c: FarmCtx, manage: ManageHooks | null = null) {
  void dispatchAction('ui.forager', async () => {
    // Both boards up front: the counter is one walk to the hut, and the seed
    // press is a tab on it, not a second trip. Two GETs beat a spinner on tab.
    const [board, seeds] = await Promise.all([foragerApi.board(), fieldApi.seeds()]);
    render(c, board, null, seeds, 'trade', manage);
  }).catch((e) => { if (!isDropped(e)) toast('The forager is not at his counter', 'error'); });
}

/**
 * Move / Demolish for the hut itself, below the counter and behind a rule line.
 *
 * Below, not above: trading is the reason the panel opens and must stay the first
 * thing under the thumb, while relocating or destroying the hut is rare and one of
 * them is irreversible. Demolish keeps its confirm dialog (the hook owns it), so
 * a mis-tap here still costs nothing.
 */
export function manageFooter(manage: ManageHooks | null): HTMLElement | null {
  if (!manage) return null;
  const btn = (label: string, cls: string, onClick: () => void) =>
    el('button', `flex-1 min-h-[44px] px-3 rounded border text-sm ${cls}`, { onClick }, label);
  return el('div', 'mt-3 pt-3 border-t border-border flex gap-2 flex-wrap shrink-0', {},
    btn('Move it', 'border-border text-text-dim hover:border-gold hover:text-gold', manage.onMove),
    btn(`Demolish ${manage.name}`, 'border-error text-error hover:bg-error/10', manage.onDemolish));
}

function render(c: FarmCtx, board: ForagerBoard, offer: Offer | null, seeds: SeedBoard, tab: Tab = 'trade',
                manage: ManageHooks | null = null) {
  // Re-render in place on every click: the panel is ~60 tiny nodes and the state
  // (which stack is on the counter, how many) is one object, so rebuilding is far
  // less code than patching, and cannot drift from the board it was priced off.
  const redraw = (next: Offer | null) => render(c, board, next, seeds, tab, manage);
  const swap = (next: Tab) => render(c, board, null, seeds, next, manage);

  // The CARD no longer scrolls — it is a fixed-height column, and only the goods
  // list inside it moves.
  //
  // It used to be `max-h-[86vh] overflow-y-auto`, so the whole panel scrolled as
  // one: the counter (what you are about to sell, the price, the Confirm button)
  // scrolled away with the shelves, and on a phone the thing you were in the middle
  // of doing was routinely off screen. `min-h-0` is load-bearing — a flex child
  // defaults to min-height:auto and refuses to shrink, which makes an inner
  // scroller impossible.
  const card = el('div',
    'bg-surface border border-border rounded-lg p-4 w-[640px] max-w-[94vw] max-h-[86vh] '
    + 'flex flex-col min-h-0 overflow-hidden shadow-lift sheet-card', {},
    el('div', 'flex justify-between items-center mb-1 shrink-0', {},
      el('h3', 'font-heading text-gold inline-flex items-center gap-1.5', {},
        el('img', 'w-5 h-5 object-contain', { src: '/sprites/npc/forager.png', style: 'image-rendering:pixelated' }),
        'The Forager'),
      el('button', 'tb-close-x text-text-dim hover:text-text text-lg', { onClick: () => closePlanter(c) }, '×')),
    el('div', 'text-[11px] text-text-dim mb-3 shrink-0', {},
      'Push goods across the counter and he names his price. He buys low and sells high — trading in a circle only makes him rich.'));

  card.append(tabs(tab, swap));
  if (tab === 'seeds') {
    // Same rule as the trade tab: the LIST scrolls, the chrome does not.
    const seedScroller = el('div', 'flex-1 min-h-0 overflow-y-auto -mx-1 px-1', {},
      seedPanel(c, seeds, (fresh) => render(c, board, null, fresh, 'seeds', manage)));
    seedScroller.dataset.scroller = 'seeds';
    card.append(seedScroller);
    const prevSeeds = c.overlay?.querySelector('[data-scroller="seeds"]') as HTMLElement | null;
    if (prevSeeds) {
      const top = prevSeeds.scrollTop;
      requestAnimationFrame(() => { seedScroller.scrollTop = top; });
    }
    // Both tabs carry the footer: the hut's controls must not depend on which half
    // of the counter you happen to be looking at.
    const seedFooter = manageFooter(manage);
    if (seedFooter) card.append(seedFooter);
    closePlanter(c);
    const seedWrap = clearWrap(() => closePlanter(c), card);
    playSfx('ui_open');
    openOverlay(c, seedWrap);
    return;
  }

  // ── Your side: everything you hold that he will take ──────────────────────
  const mine = board.sell.filter((i) => i.owned > 0);
  const shelf = board.stock;

  const columns = el('div', 'flex gap-3 flex-wrap');
  columns.append(
    column('Your bag', mine.length ? mine : null,
      'Nothing he wants yet — harvest, mine or hunt, then come back.',
      (it) => redraw({ side: 'sell', item: it, qty: 1 }),
      (it) => redraw({ side: 'sell', item: it, qty: coinSellable(it.owned, it.coinSellable) }),
      offer?.side === 'sell' ? offer.item.itemId : null,
      'pays', true),
    column('His shelf', shelf.length ? shelf : null, 'His shelf is bare today.',
      (it) => redraw({ side: 'buy', item: it, qty: 1 }),
      // A buy has no stack to take "all" of — his supply is unlimited — so the
      // bulk chip is a round ten, which is what a build shortfall actually looks
      // like ("I need 12 more stone").
      (it) => redraw({ side: 'buy', item: it, qty: 10 }),
      offer?.side === 'buy' ? offer.item.itemId : null,
      'wants'),
  );
  // The Shroom shelf, only when the server sent one — an older backend simply has
  // no `shrooms` on the board and the counter keeps its two columns.
  const premium = board.shrooms ?? [];
  if (premium.length) {
    columns.append(
      column('Under the counter · Shrooms', premium, 'Nothing under the counter today.',
        (it) => redraw({ side: 'shrooms', item: it, qty: 1 }),
        (it) => redraw({ side: 'shrooms', item: it, qty: 10 }),
        offer?.side === 'shrooms' ? offer.item.itemId : null,
        'costs', false, 'Shrooms'),
    );
  }
  // Goods you bought with Shrooms. Owner (2026-09-24): "if I purchased 5 but I
  // have 35, it should be divided in the shop — 5 for Shrooms minus 4%, and 30
  // only for Coins." These are the 5; the bag column above sells the 30.
  const back = board.sellBack ?? [];
  if (back.length) {
    const commission = board.sellBackCommission ?? 0;
    const asItem = (r: SellBackRow): TradeItem => ({
      itemId: r.itemId, name: r.name, owned: r.units,
      // Per-unit, for the tile. The counter prices the exact quantity.
      unit: sellBackQuote(r, 1, commission).net,
    });
    const rowOf = (it: TradeItem) => back.find((r) => r.itemId === it.itemId)!;
    columns.append(
      column('Bought with Shrooms · sells back for Shrooms',
        back.map(asItem), '',
        (it) => redraw({ side: 'sellback', item: it, qty: 1, back: { row: rowOf(it), commission } }),
        (it) => redraw({ side: 'sellback', item: it, qty: it.owned, back: { row: rowOf(it), commission } }),
        offer?.side === 'sellback' ? offer.item.itemId : null,
        'refunds', true, 'Shrooms'),
    );
  }
  // The one scrolling region. `shrink-0` on everything else keeps the header, the
  // counter and the footer pinned around it.
  const scroller = el('div', 'flex-1 min-h-0 overflow-y-auto -mx-1 px-1', {}, columns);
  scroller.dataset.scroller = 'goods';
  card.append(scroller);

  // Restore where the player was.
  //
  // Every quantity tap calls redraw(), which rebuilds the panel from the offer
  // object — cheap and impossible to desync, but it also meant a fresh element with
  // scrollTop 0. Tapping "+" on an item halfway down the bag threw the list back to
  // the top, so adjusting a quantity cost you your place every single time. The
  // previous scroller is still mounted when this runs, so its position can be read
  // and carried over.
  const prev = c.overlay?.querySelector('[data-scroller="goods"]') as HTMLElement | null;
  if (prev) {
    const top = prev.scrollTop;
    // After append, before paint.
    requestAnimationFrame(() => { scroller.scrollTop = top; });
  }

  card.append(counter(c, offer, redraw, manage));
  const footer = manageFooter(manage);
  if (footer) card.append(footer);

  closePlanter(c);
  // No dimming: this dialog opens while you are standing at the hut, and greying
  // out the farm behind it is exactly what the owner asked us to stop doing.
  const wrap = clearWrap(() => closePlanter(c), card);
  playSfx('ui_open');
  // openOverlay (not a bare append): it attaches Escape, so the panel always has
  // a way out even when the chrome its × sits under is hidden.
  openOverlay(c, wrap);
}

/** One side of the counter: a title and a grid of slot cells. */
function column(
  title: string,
  items: TradeItem[] | null,
  empty: string,
  onOne: (it: TradeItem) => void,
  onAll: (it: TradeItem) => void,
  selectedId: string | null,
  verb: string,
  /**
   * Whose stock the quantity badge describes.
   *
   * `TradeItem.owned` is always what YOU hold — the board sends it for every row
   * so the bag column can grey out what you have none of. This column drew that
   * badge on EVERY shelf, including the keeper's, where it read as "he has 12 of
   * these". Owner: "on the forager, his shelf provides your item and quantity,
   * according to that you can buy infinite amount of what you have." Exactly: the
   * badge implied a stock level, and then the counter let you buy past it, because
   * his supply is genuinely unlimited and never was the number on the tile.
   *
   * So the badge belongs only to the column that is actually yours.
   */
  mine = false,
  /** Coins on the keeper's coin shelves; Shrooms under the counter. */
  currency = 'Coins',
): HTMLElement {
  // The glyph has to follow the currency the column trades in.
  //
  // The price under every tile was a hardcoded 🪙, so the Shroom shelf — the one
  // priced in a currency you buy with REAL MONEY — quoted its prices in coins.
  // The counter below the shelves already got this right ("the one price that must
  // never be ambiguous"); the tiles it was quoting did not. Owner: "Under the
  // counter · Shrooms provides coins images."
  const glyph = currency === 'Shrooms' ? '🍄' : '🪙';
  const grid = el('div', 'flex flex-wrap content-start gap-1.5 min-h-[92px]');
  for (const it of items ?? []) {
    const selected = it.itemId === selectedId;
    const cell = slotCell(`${it.name} — he ${verb} ${it.unit} ${currency} each`,
      pxImg(itemIconSrc(it.itemId)),
      ...(mine && it.owned > 0 ? [qtyBadge(it.owned)] : []));
    cell.classList.add('cursor-pointer', 'rounded');
    if (selected) cell.classList.add('ring-2', 'ring-gold');
    // Click = one unit; shift-click = the lot. The "All" chip below carries the
    // same action for touch, where there is no shift key.
    cell.addEventListener('click', (e) => ((e as MouseEvent).shiftKey ? onAll(it) : onOne(it)));
    const price = el('div', 'text-[10px] text-text-dim font-mono text-center leading-tight inline-flex items-center gap-0.5', {},
      ...richText(`${glyph} ${it.unit}`));
    const all = el('button', 'text-[10px] px-1 rounded border border-border text-text-dim hover:border-gold hover:text-gold',
      { onClick: () => onAll(it) }, 'All');
    grid.append(el('div', 'flex flex-col items-center gap-0.5 w-11', {}, cell, price, all));
  }
  if (!items) grid.append(el('div', 'text-[12px] text-text-dim p-1', {}, empty));
  return el('div', 'flex-1 min-w-[240px]', {},
    el('div', 'text-[11px] uppercase tracking-wide text-text-dim mb-1', {}, title),
    grid);
}

/**
 * The counter itself: what is being moved, how much of it, and the exact Coins.
 *
 * `unit` came from the server's board and `total` is unit × qty — the same
 * arithmetic the server does — so what is printed here is what lands.
 */
function counter(c: FarmCtx, offer: Offer | null,
                 redraw: (o: Offer | null) => void, manage: ManageHooks | null): HTMLElement {
  // shrink-0: the counter is pinned below the scrolling list and must keep its
  // full height — squeezing IT instead of the list is what put the Confirm button
  // off screen on a phone.
  const box = el('div', 'mt-3 pt-3 border-t border-border shrink-0');
  if (!offer) {
    box.append(el('div', 'text-[12px] text-text-dim text-center py-2', {},
      'Click something on either side to put it on the counter.'));
    return box;
  }

  // A sell is capped by what you actually hold; a buy is capped only by the
  // per-trade limit. Clamping here keeps the printed total honest — the server
  // would refuse an over-sell anyway, but only after you pressed the button.
  // A Coin sale is capped at the part of the pile that was NOT bought with
  // Shrooms; a sell-back at the part that was.
  const max = offer.side === 'sell' ? coinSellable(offer.item.owned, offer.item.coinSellable)
    : offer.side === 'sellback' ? offer.item.owned : 999;
  if (max < 1) {
    box.append(el('div', 'text-[12px] text-text-dim text-center py-2', {},
      `Every ${offer.item.name} you hold was bought with Shrooms — sell them back under “Bought with Shrooms”.`));
    return box;
  }
  const qty = Math.max(1, Math.min(offer.qty, max));
  const quote = offer.back ? sellBackQuote(offer.back.row, qty, offer.back.commission) : null;
  const total = quote ? quote.net : offer.item.unit * qty;
  const selling = offer.side === 'sell' || offer.side === 'sellback';

  const step = (d: number) => el('button',
    'w-7 h-7 rounded border border-border text-sm hover:border-gold hover:text-gold disabled:opacity-40',
    { onClick: () => redraw({ ...offer, qty: qty + d }) }, d > 0 ? '+' : '−');
  const bump = (n: number, label: string) => el('button',
    'px-1.5 py-0.5 rounded border border-border text-[11px] hover:border-gold hover:text-gold',
    { onClick: () => redraw({ ...offer, qty: n }) }, label);

  // The shroom shelf is priced in SHROOMS. Quoting it with the coin glyph would be
  // the panel lying about which balance is about to be spent — and Shrooms are
  // bought with real money, so that is the one price that must never be ambiguous.
  const premium = offer.side === 'shrooms' || offer.side === 'sellback';
  const coin = premium ? '🍄' : '🪙';
  const go = el('button',
    `px-3 py-1.5 rounded border text-sm ${offer.side === 'sellback' ? 'border-shroom text-shroom hover:bg-shroom/10' : selling ? 'border-gold text-gold hover:bg-gold/10' : 'border-shroom text-shroom hover:bg-shroom/10'}`,
    { onClick: () => void commit(c, { ...offer, qty }, redraw, manage) },
    ...richText(selling ? `Sell for ${coin} ${total}` : `Buy for ${coin} ${total}`));

  // A sell-back shows only what lands. Owner (2026-10-19): "don't show fee …
  // just direct end price" — the fee is already inside `quote.net`.
  const detail = quote
    ? `he pays you ${coin} ${quote.net}`
    : `${coin} ${offer.item.unit} each · ${selling ? 'he pays you' : 'you pay him'} ${coin} ${total}`;

  box.append(el('div', 'flex items-center gap-3 flex-wrap', {},
    slotCell(offer.item.name, pxImg(itemIconSrc(offer.item.itemId)), qtyBadge(qty)),
    el('div', 'flex-1 min-w-[150px]', {},
      el('div', 'text-sm', {}, offer.item.name),
      el('div', 'text-[11px] text-text-dim font-mono inline-flex items-center gap-1', {},
        ...richText(detail))),
    el('div', 'flex items-center gap-1', {}, step(-1), step(+1), bump(10, '10'),
      selling ? bump(max, 'All') : bump(100, '100')),
    go,
    el('button', 'px-2 py-1 rounded border border-border text-text-dim hover:text-text text-sm',
      { onClick: () => redraw(null) }, 'Clear')));
  return box;
}

async function commit(c: FarmCtx, offer: Offer, redraw: (o: Offer | null) => void,
                      manage: ManageHooks | null) {
  const { side, item, qty } = offer;
  if (qty < 1) return;
  try {
    await dispatchAction(`forager.${side}`, async (actionId) => {
      if (side === 'sellback') {
        const r = await foragerApi.sellBack(item.itemId, qty, actionId);
        toast(`Sold ${r.qty}× ${item.name} for 🍄 ${r.net}`);
      } else {
        const r = side === 'sell'
          ? await foragerApi.sell(item.itemId, qty, actionId)
          : side === 'shrooms'
            ? await foragerApi.buyShrooms(item.itemId, qty, actionId)
            : await foragerApi.buy(item.itemId, qty, actionId);
        // The glyph follows the currency: a Shroom purchase was toasting coins.
        const glyph = side === 'shrooms' ? '🍄' : '🪙';
        toast(side === 'sell'
          ? `Sold ${r.qty}× ${item.name} for ${glyph} ${r.total}`
          : `Bought ${r.qty}× ${item.name} for ${glyph} ${r.total}`);
      }
      // Anything touching resources or currency refreshes BOTH: the HUD owns the
      // coin readout, the bag owns the counts this panel is priced against.
      await c.game.refreshHud();
      refreshBag(c);
      // Re-price from the server rather than patching owned counts locally — a
      // day rollover mid-session would otherwise leave stale numbers on screen.
      // The seed board is re-read with it: a sale changes the produce it prices.
      const [fresh, freshSeeds] = await Promise.all([foragerApi.board(), fieldApi.seeds()]);
      // `manage` is carried through the re-price: a trade must not be the thing
      // that strips the hut's own controls off the panel.
      render(c, fresh, null, freshSeeds, 'trade', manage);
    }, { key: `${side}:${item.itemId}:${qty}` });
  } catch (e) {
    if (isDropped(e)) return;
    toast(e instanceof ApiError ? e.message : 'The trade fell through', 'error');
    redraw(null);
  }
}

/** The two halves of the counter. 44px tall — these are thumb targets. */
function tabs(active: Tab, swap: (t: Tab) => void): HTMLElement {
  const chip = (id: Tab, label: string) => el('button',
    `flex-1 min-h-[44px] px-3 rounded border text-sm ${
      active === id ? 'border-gold text-gold bg-gold/10' : 'border-border text-text-dim hover:text-text'}`,
    { onClick: () => swap(id) }, label);
  return el('div', 'flex gap-2 mb-3 shrink-0', {}, chip('trade', 'Trade'), chip('seeds', 'Seed press'));
}

/**
 * The seed press.
 *
 * Every number on it comes from GET /field/seeds — the rate, what you hold, how
 * many you could press. Nothing is computed here: if the client derived the
 * ratio it would eventually quote one number while the server charged another.
 *
 * It is also the SEED SHOP (owner, 2026-08-17: "I can't create or buy seeds in
 * the shop… create the seeds in market, define cost for them"). Every unlocked
 * crop is listed with a Buy button at the crop's own seedCost — the same price
 * planting charges when your bag is empty, so buying here is that payment made a
 * step early, never a discount or a surcharge. Locked crops are left out: the
 * server refuses them (buySeed checks unlockLevel), and a shelf of things that
 * bounce is worse than a short shelf.
 *
 * Rows are the crops you can BUY (level-unlocked) plus any you hold produce or
 * seed for. A row you can neither press nor buy would be a wall of zeroes.
 */
function seedPanel(c: FarmCtx, board: SeedBoard, reload: (b: SeedBoard) => void): HTMLElement {
  const box = el('div', '');
  box.append(el('div', 'text-[11px] text-text-dim mb-2', {},
    'Buy seed for Coins at the same price planting charges, or press your own harvest back into seed. '
    + 'The press is a poor rate on purpose — a seed always costs more produce than one planting gives back, '
    + 'so it is the way out of an empty purse, not a way to farm seeds.'));

  const usable = board.rates.filter((r) =>
    r.produceOwned > 0 || r.seedsOwned > 0 || r.unlockLevel <= board.level);
  if (!usable.length) {
    box.append(el('div', 'text-[12px] text-text-dim p-2', {},
      'Nothing to press yet — harvest something first.'));
    return box;
  }

  const rows = el('div', 'flex flex-col gap-1.5');
  for (const r of usable) rows.append(seedRow(c, r, board.level, reload));
  box.append(rows);
  return box;
}

function seedRow(c: FarmCtx, r: SeedRate, level: number, reload: (b: SeedBoard) => void): HTMLElement {
  const enough = r.canMake >= 1;
  const press = el('button',
    `min-h-[44px] min-w-[44px] px-3 rounded border text-sm ${
      enough ? 'border-gold text-gold hover:bg-gold/10' : 'border-border text-text-dim opacity-50'}`,
    { onClick: () => { if (enough) void makeSeed(c, r, reload); } },
    'Press 1');
  if (!enough) press.setAttribute('disabled', 'true');

  // The price is the SERVER's number (SeedRate.seedCost, from GET /field/seeds) —
  // the client never invents one, and /forager/buy-seed re-derives the same value
  // from the same crop table before it takes a coin.
  const unlocked = r.unlockLevel <= level;
  const buy = el('button',
    `min-h-[44px] px-3 rounded border text-sm ${
      unlocked ? 'border-gold text-gold hover:bg-gold/10' : 'border-border text-text-dim opacity-50'}`,
    { onClick: () => { if (unlocked) void buySeed(c, r, reload); } },
    // richText, NOT a raw string: 🪙 is a token that ui/icons.ts swaps for the
    // pack's coin sprite. Passed as plain text it reaches the DOM as the system
    // emoji, which is the one place on this panel that happened — owner: "seeds
    // press now provides emoji icon for coins, should be our coins image."
    ...richText(`Buy 🪙 ${r.seedCost}`));
  if (!unlocked) buy.setAttribute('disabled', 'true');

  return el('div', 'flex items-center gap-2 flex-wrap p-1.5 rounded border border-border', {},
    slotCell(`${r.name} — ${r.produceForOneSeed} per seed`,
      pxImg(itemIconSrc(r.produceItemId)), qtyBadge(r.produceOwned)),
    el('div', 'flex-1 min-w-[150px]', {},
      el('div', 'text-sm', {}, r.name),
      el('div', 'text-[11px] text-text-dim font-mono', {},
        `${r.produceForOneSeed} produce → 1 seed · you hold ${r.seedsOwned} seed${r.seedsOwned === 1 ? '' : 's'}`
        + (unlocked ? '' : ` · locked until Lv${r.unlockLevel}`))),
    buy, press);
}

/** Buy one seed for Coins. Same reload-don't-patch rule as the press. */
async function buySeed(c: FarmCtx, r: SeedRate, reload: (b: SeedBoard) => void) {
  try {
    await dispatchAction('forager.buySeed', async (actionId) => {
      const bought = await foragerApi.buySeed(r.seedItemId, 1, actionId);
      toast(`Bought 1× ${r.name} seed for 🪙 ${bought.total}`);
      // Coins moved, so the HUD readout must move with it — same rule as trade().
      await c.game.refreshHud();
      refreshBag(c);
      reload(await fieldApi.seeds());
    }, { key: `buyseed:${r.cropId}` });
  } catch (e) {
    if (isDropped(e)) return;
    toast(e instanceof ApiError ? e.message : 'The keeper is out of that seed', 'error');
  }
}

async function makeSeed(c: FarmCtx, r: SeedRate, reload: (b: SeedBoard) => void) {
  try {
    await dispatchAction('field.seeds', async (actionId) => {
      const made = await fieldApi.extractSeeds(r.cropId, 1, actionId);
      toast(`Pressed ${made.seeds}× ${r.name} seed for ${made.produceSpent} ${r.name}`);
      refreshBag(c);
      // Re-read rather than patch the counts locally: the press is the only thing
      // on this panel priced off the bag, and a stale count would offer a press
      // the server is about to refuse.
      reload(await fieldApi.seeds());
    }, { key: `seed:${r.cropId}` });
  } catch (e) {
    if (isDropped(e)) return;
    toast(e instanceof ApiError ? e.message : 'The press jammed', 'error');
  }
}
